import {
  DeviceResult,
  deviceFail,
  deviceOk,
  isBrowser,
} from "./types";

/**
 * Lo que se captura en cada lectura. La **precisión es tan importante como las
 * coordenadas** (§8.3.3): una marca "dentro del radio" con precisión de 2 km no
 * prueba nada, y el servidor necesita el dato para poder decirlo.
 */
export type DevicePosition = {
  latitude: number;
  longitude: number;
  /** Radio de incertidumbre en metros que reporta el dispositivo. */
  accuracy_m: number;
  /** Reloj del teléfono al momento de la lectura. El servidor guarda el suyo aparte (§8.3.2). */
  device_time: string;
};

export type PositionOptions = {
  /** Por defecto sí: en la asistencia la precisión importa más que la batería. */
  highAccuracy?: boolean;
  timeoutMs?: number;
  /** Antigüedad máxima aceptable de una lectura en caché. Por defecto 0: siempre fresca. */
  maxAgeMs?: number;
};

const DEFAULT_TIMEOUT_MS = 12_000;

const toDevicePosition = (position: GeolocationPosition): DevicePosition => ({
  latitude: position.coords.latitude,
  longitude: position.coords.longitude,
  accuracy_m: position.coords.accuracy,
  device_time: new Date(position.timestamp).toISOString(),
});

const describeError = (
  error: GeolocationPositionError,
): DeviceResult<DevicePosition> => {
  if (error.code === error.PERMISSION_DENIED) {
    return deviceFail("denied", "El permiso de ubicación está desactivado.");
  }
  if (error.code === error.TIMEOUT) {
    return deviceFail("timeout", "El GPS no respondió a tiempo.");
  }
  return deviceFail("unavailable", "No se pudo obtener la ubicación.");
};

/**
 * Nunca lanza. Quien la llama decide qué hacer con el fallo, y en asistencia la
 * respuesta correcta es **marcar igual y registrar que no hubo coordenadas**:
 * impedir una marca por un problema técnico genera un conflicto laboral peor que
 * el que se intenta evitar (§8.3.3).
 */
export function getCurrentPosition(
  options: PositionOptions = {},
): Promise<DeviceResult<DevicePosition>> {
  if (!isBrowser() || !("geolocation" in navigator)) {
    return Promise.resolve(
      deviceFail<DevicePosition>(
        "unsupported",
        "Este dispositivo no ofrece ubicación.",
      ),
    );
  }

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(deviceOk(toDevicePosition(position))),
      (error) => resolve(describeError(error)),
      {
        enableHighAccuracy: options.highAccuracy ?? true,
        timeout: options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
        maximumAge: options.maxAgeMs ?? 0,
      },
    );
  });
}

/**
 * Seguimiento continuo, para las pantallas que muestran "estás a X metros del sitio"
 * mientras el empleado se acerca. Devuelve la función para detenerlo — dejarlo vivo
 * vacía la batería, así que siempre se llama al desmontar.
 */
export function watchPosition(
  listener: (result: DeviceResult<DevicePosition>) => void,
  options: PositionOptions = {},
): () => void {
  if (!isBrowser() || !("geolocation" in navigator)) {
    listener(
      deviceFail<DevicePosition>(
        "unsupported",
        "Este dispositivo no ofrece ubicación.",
      ),
    );
    return () => {};
  }

  const watchId = navigator.geolocation.watchPosition(
    (position) => listener(deviceOk(toDevicePosition(position))),
    (error) => listener(describeError(error)),
    {
      enableHighAccuracy: options.highAccuracy ?? true,
      timeout: options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
      maximumAge: options.maxAgeMs ?? 0,
    },
  );

  return () => navigator.geolocation.clearWatch(watchId);
}

export type LocationPermission = "granted" | "denied" | "prompt" | "unknown";

/**
 * Permite explicar el estado antes de pedirlo (Safari no implementa Permissions API
 * para geolocalización, de ahí `"unknown"`, que se trata como `"prompt"`).
 */
export async function getLocationPermission(): Promise<LocationPermission> {
  if (!isBrowser() || !navigator.permissions?.query) return "unknown";
  try {
    const status = await navigator.permissions.query({
      name: "geolocation" as PermissionName,
    });
    return status.state as LocationPermission;
  } catch {
    return "unknown";
  }
}

/**
 * Distancia en metros entre dos puntos (Haversine).
 *
 * Se usa **solo para la interfaz** —mostrar cuán lejos está el empleado del sitio—.
 * La validación contra la geocerca es del servidor (§8.3.3): un cálculo en el cliente
 * es sugerencia, no autoridad.
 */
export function distanceInMeters(
  from: { latitude: number; longitude: number },
  to: { latitude: number; longitude: number },
): number {
  const EARTH_RADIUS_M = 6_371_000;
  const toRad = (degrees: number) => (degrees * Math.PI) / 180;

  const dLat = toRad(to.latitude - from.latitude);
  const dLon = toRad(to.longitude - from.longitude);
  const lat1 = toRad(from.latitude);
  const lat2 = toRad(to.latitude);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.sin(dLon / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);

  return Math.round(2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a)));
}
